import { Button, Text, ThemeToggle } from "@microbeta/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdClose, MdMenu, MdNotifications, MdStar } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import config from "../../../../config";
import { useAuthContext } from "../../../../features/auth/AuthContext";
import { usePurchases } from "../../../../features/purchases";
import { useUnreadNotificationCount } from "../../../../features/social/api/queries";
import { useAnalytics } from "../../../../shared/hooks";
import { useNavigationLinks } from "../../../hooks/useNavigationLinks";
import { paths } from "../../../utils/routing";
import { LanguageDropdownItem } from "../../ui/LanguageSelector";
import styles from "./hamburger-menu.module.scss";

interface HamburgerMenuProps {
  className?: string;
}

export const HamburgerMenu = ({ className }: HamburgerMenuProps) => {
  const { authState } = useAuthContext();
  const { isPremium, getPurchaseLink } = usePurchases();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { captureEvent } = useAnalytics();
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);
  const sideMenuRef = useRef<HTMLDivElement>(null);
  const hamburgerButtonRef = useRef<HTMLButtonElement>(null);
  const menuOpenTimeRef = useRef<number | null>(null);
  const { navLinks, isNavLinkActive } = useNavigationLinks();
  const { data: unreadData } = useUnreadNotificationCount();
  const unreadCount = unreadData?.count ?? 0;

  // Check if we're on the homepage
  const isHomePage = location.pathname === paths.index;

  // Handle side menu close
  const handleSideMenuClose = useCallback(
    (source: string) => {
      if (isSideMenuOpen) {
        const duration = menuOpenTimeRef.current
          ? Date.now() - menuOpenTimeRef.current
          : 0;
        captureEvent("side_menu_closed", {
          user_id: authState.userId,
          source,
          duration_ms: duration,
          current_page: location.pathname,
        });
        menuOpenTimeRef.current = null;
      }
      setIsSideMenuOpen(false);
    },
    [authState.userId, isSideMenuOpen, location.pathname, captureEvent]
  );

  // Handle hamburger menu toggle
  const handleHamburgerToggle = () => {
    const newOpenState = !isSideMenuOpen;
    setIsSideMenuOpen(newOpenState);

    if (newOpenState) {
      menuOpenTimeRef.current = Date.now();
      captureEvent("side_menu_opened", {
        user_id: authState.userId,
        source: "hamburger_button",
        current_page: location.pathname,
        is_homepage: isHomePage,
      });
    } else {
      handleSideMenuClose("hamburger_button");
    }
  };

  const handlePurchaseClick = () => {
    const purchaseLink = getPurchaseLink();

    if (!purchaseLink) {
      console.error(
        "[Navigation] Purchase link not available. Please configure VITE_REVENUECAT_PURCHASE_LINK_URL"
      );
      return;
    }

    captureEvent("purchase_link_clicked", {
      user_id: authState.userId,
      is_premium: isPremium,
      source: "hamburger_menu",
    });

    // Open RevenueCat Web Purchase Link in new window
    window.open(purchaseLink, "_blank", "noopener,noreferrer");
    handleSideMenuClose("purchase_click");
  };

  // Handle side menu navigation
  const handleSideMenuNavigation = (path: string, navKey: string) => {
    const duration = menuOpenTimeRef.current
      ? Date.now() - menuOpenTimeRef.current
      : 0;

    const navLink = navLinks.find((link) => link.key === navKey);
    if (navLink) {
      captureEvent("side_menu_navigation_clicked", {
        user_id: authState.userId,
        destination: path,
        navigation_key: navKey,
        previous_page: location.pathname,
        menu_open_duration_ms: duration,
        is_active_link: isNavLinkActive(navLink),
      });
    }

    navigate(path);
    handleSideMenuClose("navigation_click");
  };

  // Close side menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close if clicking the hamburger button itself (it has its own toggle logic)
      const target = event.target as Node;
      if (
        isSideMenuOpen &&
        sideMenuRef.current &&
        !sideMenuRef.current.contains(target) &&
        hamburgerButtonRef.current &&
        !hamburgerButtonRef.current.contains(target) &&
        !(
          target instanceof Element &&
          target.closest(`.${styles.hamburgerButton}`)
        )
      ) {
        handleSideMenuClose("click_outside");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSideMenuOpen, handleSideMenuClose]);

  // Close side menu on route change only (not on menu state changes)
  const prevPathnameRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathnameRef.current !== location.pathname) {
      prevPathnameRef.current = location.pathname;
      if (isSideMenuOpen) {
        handleSideMenuClose("route_change");
      }
    }
  }, [location.pathname, isSideMenuOpen, handleSideMenuClose]);

  // Prevent body scroll when side menu is open
  useEffect(() => {
    if (isSideMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isSideMenuOpen]);

  const handleOverlayKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      handleSideMenuClose("overlay_click");
    }
  };

  return (
    <>
      {/* Hamburger Menu Button */}
      <Button
        ref={hamburgerButtonRef}
        variant="ghost"
        size="small"
        className={`${styles.hamburgerButton} ${className || ""}`}
        onClick={(e) => {
          e.stopPropagation();
          handleHamburgerToggle();
        }}
        aria-label={
          isSideMenuOpen ? t("navigation.closeMenu") : t("navigation.openMenu")
        }
      >
        {isSideMenuOpen ? <MdClose /> : <MdMenu />}
      </Button>

      {/* Overlay */}
      {isSideMenuOpen && (
        <button
          type="button"
          aria-label={t("rootLayout.closeMenu", "Close Menu")}
          className={styles.overlay}
          onClick={() => handleSideMenuClose("overlay_click")}
          onKeyDown={handleOverlayKeyDown}
        />
      )}

      {/* Side Menu */}
      <div
        ref={sideMenuRef}
        className={`${styles.sideMenu} ${isSideMenuOpen ? styles.sideMenuOpen : ""}`}
      >
        <div className={styles.sideMenuHeader}>
          <Text variant="large" className={styles.sideMenuTitle}>
            {t("navigation.menu")}
          </Text>
          <Button
            variant="ghost"
            size="small"
            className={styles.sideMenuCloseButton}
            onClick={() => handleSideMenuClose("close_button")}
            aria-label={t("navigation.closeMenu")}
          >
            <MdClose />
          </Button>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "var(--spacing-xs)",
            padding: "0 var(--spacing-md)",
            marginTop: "var(--spacing-md)",
          }}
        >
          <LanguageDropdownItem />
          <ThemeToggle />
        </div>
        <div className={styles.sideMenuContent}>
          {config.FEATURE_FLAG_SHOW_PAYMENTS && !isPremium && (
            <Button
              variant="primary"
              className={styles.mobileUpgradeButton}
              onClick={handlePurchaseClick}
            >
              <MdStar className={styles.sideNavIcon} />
              <Text variant="medium">{t("navigation.upgrade", "Upgrade")}</Text>
            </Button>
          )}
          <Button
            variant="ghost"
            className={styles.sideNavLink}
            onClick={() =>
              handleSideMenuNavigation(
                paths.social.notifications,
                "notifications"
              )
            }
          >
            <span className={styles.sideNavIcon}>
              <MdNotifications />
            </span>
            <Text variant="medium" style={{ flex: 1, textAlign: "left" }}>
              {t("navigation.notifications", "Notifications")}
            </Text>
            {unreadCount > 0 && (
              <span
                className={styles.badge}
                style={{
                  position: "static",
                  backgroundColor: "var(--color-status-error)",
                  color: "white",
                  padding: "0 6px",
                  borderRadius: "10px",
                  fontSize: "0.75rem",
                  fontWeight: "bold",
                }}
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </Button>
          {navLinks.map((navLink) => (
            <Button
              variant="ghost"
              key={navLink.key}
              onClick={() =>
                handleSideMenuNavigation(navLink.path, navLink.key)
              }
              className={`${styles.sideNavLink} ${
                isNavLinkActive(navLink) ? styles.sideNavLinkActive : ""
              }`}
            >
              <span className={styles.sideNavIcon}>{navLink.icon}</span>
              <Text variant="medium" className={styles.sideNavLabel}>
                {t(navLink.labelKey)}
              </Text>
            </Button>
          ))}
        </div>
      </div>
    </>
  );
};
